<template>
	<div class="tables-select">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)">
			<div class="tables-select-content" v-if="active">
				<ModalHeader
					:title="isDelivery ? getString('delivery') : userStore.takeawayWord"
					:button-text="getString('confirm')"
					@clickButton="confirmNotInPlace"
				/>
				<AppList :items="items">
					<template #customers-slot="{item}">
						<div class="row centered space-between">
							<p class="list-item-title">{{ item.title }}</p>
							<AppButton outline :text="item.button_text" @click="item.callback" :disabled="item.disabled" />
						</div>
						<CustomersDisplay small />
					</template>
					<template v-slot:input-text="{item}">
						<div class="row centered space-between list-row">
							<p class="list-item-title">{{ item.title }}</p>
							<AppTextInput :ref="item.ref" class="text-input" :placeholder="item.placeholder" :type="item.inputType" :value="item.initialValue" />
						</div>
					</template>
					<template v-slot:input-address="{item}">
						<div class="row centered space-between list-row">
							<p class="list-item-title">{{ item.title }}</p>
							<AppGoogleMapsInput class="text-input" :placeholder="item.placeholder" no-map @changed-address="(a) => address = a" :value="item.initialValue" />
						</div>
					</template>
					<template v-slot:input-calendar="{item}">
						<div class="row centered space-between list-row">
							<p class="list-item-title">{{ item.title }}</p>
							<DatePicker ref="date-picker" v-model="date" :mode="item.mode" is24hr color="teal" :min-date='new Date()' :is-dark="userStore.darkMode">
								<template #default="{ togglePopover }">
									<AppTextInput class="text-input" @click="togglePopover()" :value="item.format" readonly />
								</template>
							</DatePicker>
						</div>
					</template>
				</AppList>
			</div>
		</AppBottomSheet>
		<CustomersPicker :active="customerPicker" @toggling-popup="togglingCustomersPicker" @picked-customer="pickedCustomer" />
		<AppNotification ref="invalid-address" :title="languageStore.getString('error')" :body-text="languageStore.getString('invalid_address')" :type="-2" />
		<AppNotification ref="invalid-address-customer" :title="languageStore.getString('error')" :body-text="languageStore.getString('invalid_address_customer')" :type="-2" />
		<AppNotification ref="invalid-customer-name" :title="languageStore.getString('error')" :body-text="languageStore.getString('invalid_customer_name')" :type="-2" />
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useCounterStore} from "@/console-new/stores/counter.store";
import {useRoomsStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import {formatDateMMDD} from "@/shared/helpers/dates";
import {formatTimeHHmm} from "@/shared/helpers/dates";
import {validateEmail} from "@/shared/helpers/strings";
import {DatePicker} from "v-calendar";
import AppNotification from "@/shared/components/AppNotification.vue";
import AppButton from "@/shared/components/AppButton.vue";
import CustomersPicker from "@/console-new/components/ops/CustomersPicker.vue";
import CustomersDisplay from "@/console-new/components/ops/CustomersDisplay.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		const counterStore = useCounterStore();
		const roomsStore = useRoomsStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, languageStore, counterStore, roomsStore, userStore, notificationStore, svgStore};
	},
	name: "NotInPlaceForm",
	components: {CustomersPicker, AppButton, AppNotification, AppGoogleMapsInput, AppTextInput, AppList, ModalHeader, AppBottomSheet, DatePicker, CustomersDisplay},
	mixins: [WindowWidth],
	emits: ["toggling-popup"],
	props: {
		active: {
			type: Boolean,
			required: true,
		},
		type: {
			type: String,
			required: true,
			validator: (value) => {
				return ['delivery', 'takeaway'].includes(value);
			}
		},
	},
	data() {
		return {
			date: this.getDefaultDate(),
			address: null,
			customerPicker: false,
		}
	},
	computed: {
		dateFormatted() {
			return formatDateMMDD(this.date);
		},
		timeFormatted() {
			return formatTimeHHmm(this.date);
		},
		isDelivery() {
			return this.type === 'delivery';
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		items() {
			let fields = [{
				title: this.getString("associate_customer"),
				customSlot: "customers-slot",
				button_text: this.getString("customer"),
				callback: () => this.customerPicker = true,
			}];
			if(this.type === "delivery") {
				if(this.counterStore.selectedCustomer) {
					let address = this.counterStore.selectedCustomer.type === "person" ? this.counterStore.selectedCustomer.address : this.counterStore.selectedCustomer.delivery_address;
					fields.push({
						title: this.getString('delivery_address'),
						rightText: address ? address : this.getString('no_address'),
						rightPrimaryText: true,
						backgroundColor: "var(--color-alpha-1)",
					});
				} else {
					fields.push({
						title: this.getString('delivery_address'),
						placeholder: this.getString('address'),
						customSlot: 'input-address',
						initialValue: this.counterStore.deliveryAddress ? this.counterStore.deliveryAddress.address : null,
						backgroundColor: "var(--color-alpha-1)",
					});
				}
			}
			fields.push({
				title: this.getString('time'),
				mode: "time",
				format: this.timeFormatted,
				customSlot: 'input-calendar',
				backgroundColor: "var(--color-alpha-1)",
			});
			fields.push({
				title: this.getString('date'),
				mode: "date",
				format: this.dateFormatted,
				customSlot: 'input-calendar',
				backgroundColor: "var(--color-alpha-1)",
			});
			if(!this.counterStore.selectedCustomer) {
				fields.push({
					title: this.getString('customer_name'),
					placeholder: this.getString('customer_name'),
					customSlot: 'input-text',
					inputType: 'text',
					initialValue: this.counterStore.customerName ? this.counterStore.customerName : null,
					ref: 'customer-name',
					backgroundColor: "var(--color-alpha-1)",
				});
				fields.push({
					title: this.getString('customer_email'),
					placeholder: this.getString('email_optional'),
					customSlot: 'input-text',
					inputType: 'email',
					initialValue: this.counterStore.customerEmail ? this.counterStore.customerEmail : null,
					ref: 'customer-email',
				});
				fields.push({
					title: this.getString('customer_phone'),
					placeholder: this.getString('phone_optional'),
					customSlot: 'input-text',
					inputType: 'tel',
					initialValue: this.counterStore.customerPhone ? this.counterStore.customerPhone : null,
					ref: 'customer-phone',
				});
			}
			return fields;
		}
	},
	methods: {
		togglingCustomersPicker(v) {
			if(!v) {
				this.customerPicker = false;
			} else {
				//this.date = this.getDefaultDate();
			}
		},
		pickedCustomer(customer) {
			this.customerPicker = false;
			this.counterStore.selectedCustomer = customer;
		},
		confirmNotInPlace() {

			if(this.counterStore.selectedCustomer) {

				if(this.type === "delivery") {
					if(this.counterStore.selectedCustomer.type === "person" && !this.counterStore.selectedCustomer.address_id) {
						this.$refs['invalid-address-customer'].openNotification();
						return;
					} else if(this.counterStore.selectedCustomer.type === "company" && !this.counterStore.selectedCustomer.delivery_address_id) {
						this.$refs['invalid-address-customer'].openNotification();
						return;
					}
				}

				let email = this.counterStore.selectedCustomer.hasOwnProperty('email') ? this.counterStore.selectedCustomer.email : null;
				let phone = this.counterStore.selectedCustomer.hasOwnProperty('phone') ? this.counterStore.selectedCustomer.phone : null;
				this.completeNotInPlace(this.counterStore.selectedCustomer.name, email, phone);
			} else {
				if(this.type === "delivery" && !this.address) {
					this.$refs['invalid-address'].openNotification();
					return;
				}

				let customerName = this.$refs['customer-name'].currentValue;
				if(!customerName) {
					this.$refs['invalid-customer-name'].openNotification();
					return;
				}

				let customerEmail = this.$refs['customer-email'].currentValue;
				if(customerEmail && customerEmail.length > 0 && !validateEmail(customerEmail)) {
					this.notificationStore.showNotification("wrong-email")
					return;
				}

				let customerPhone = this.$refs['customer-phone'].currentValue;
				if(customerPhone && customerPhone.length > 0 && customerPhone.length < 8) {
					this.notificationStore.showNotification("wrong-phone")
					return;
				}

				this.completeNotInPlace(customerName, customerEmail, customerPhone);
			}

		},
		completeNotInPlace(customerName, customerEmail, customerPhone) {
			if(this.type === "delivery") {

				if(this.counterStore.selectedCustomer) {
					if(this.counterStore.selectedCustomer.type === "person") {
						this.counterStore.deliveryAddress = {
							address_id: this.counterStore.selectedCustomer.address_id,
							address: this.counterStore.selectedCustomer.address,
						};
					} else {
						this.counterStore.deliveryAddress = {
							address_id: this.counterStore.selectedCustomer.delivery_address_id,
							address: this.counterStore.selectedCustomer.delivery_address,
						};
					}
				} else {
					this.counterStore.deliveryAddress = this.address;
				}
			}
			this.counterStore.customerName = customerName;
			this.counterStore.customerEmail = customerEmail;
			this.counterStore.customerPhone = customerPhone;
			this.counterStore.notInPlaceDate = this.date;
			this.counterStore.selectedMode = this.type;
			this.counterStore.selectedTable = null;
			this.counterStore.coverNum = 0;
			this.counterStore.coverCharge = 0;
			this.counterStore.deliveryCosts = this.type === "delivery" && this.userStore.currBusiness.delivery_costs ? this.userStore.currBusiness.delivery_costs : 0;
			this.counterStore.takeawayCosts = this.type === "takeaway" && this.userStore.currBusiness.takeaway_costs ? this.userStore.currBusiness.takeaway_costs : 0;
			this.$emit('toggling-popup', false);
		},
		getDefaultDate() {
			if(this.counterStore.notInPlaceDate) {
				return this.counterStore.notInPlaceDate;
			}
			let date = new Date();
			let basePreparationTime = this.userStore.currBusiness?.preparation_time || 30;
			date.setMinutes(date.getMinutes() + basePreparationTime + 5);
			// round to nearest 10 minutes
			let minutesRound = date.getMinutes() % 10;
			date.setMinutes(date.getMinutes() - minutesRound);
			return date;
		}
	},
	watch: {
		//prevent from selecting a date in the past
		date() {
			if(this.date < new Date()) {
				this.date = new Date();
			}
		},
	}
}
</script>

<style scoped lang="scss">

.list-item-title {
	max-width: calc(100% - 105px);
	font-size: 16px;

	@media (max-width: 350px) {
		max-width: 100%;
	}
}

@media (max-width: 600px) {
	.text-slot {
		flex-direction: column;

		.list-item-title {
			width: 100%;
			max-width: unset;

		}
	}

}
.text-input, :deep(.address-input) {
	margin-left: auto;

	@media (max-width: 560px) {
		max-width: 200px;
	}
	@media (max-width: 480px) {
		max-width: 180px;
	}
	@media (max-width: 420px) {
		max-width: 150px;
	}
	@media (max-width: 350px) {
		margin-top: 6px;
		max-width: 100%;
		width: 100% !important;
	}
}

</style>
